"use client";

import { useEffect, useRef } from "react";
import { gsap } from "gsap";
import {
  ChevronDown,
  Circle,
  Clock,
  GitBranch,
  Hash,
  PenLine,
  Plus,
} from "lucide-react";
import { MockChrome } from "./MockWindow";

/**
 * Auto-playing eCRF Builder demo. Picks a field from the palette, drags it
 * into the form canvas, drops it onto a placeholder row that "accepts" the
 * field type. Loops with a 1.6s rest at the end of each cycle.
 *
 * Honours prefers-reduced-motion — under reduced motion the demo renders
 * the already-completed final state and skips the loop entirely.
 *
 * Stay restrained: one drag at a time, deliberate easing, no juggling.
 */

const PALETTE = [
  { icon: PenLine, label: "Text" },
  { icon: Hash, label: "Number" },
  { icon: Clock, label: "Date" },
  { icon: ChevronDown, label: "Select" },
  { icon: Circle, label: "Yes / No" },
];

export function DragDropShowcase() {
  const root = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = root.current;
    if (!el) return;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) return;

    const ctx = gsap.context(() => {
      // Build a repeating timeline that cycles through 4 field drops.
      const tl = gsap.timeline({ repeat: -1, repeatDelay: 1.6, defaults: { ease: "power2.inOut" } });

      // Each cycle: pick palette item → fly to target row → drop indicator pulses.
      const cycles = [
        { palette: ".drag-palette-0", target: ".drag-target-0", typeChip: ".type-chip-0", typeLabel: "Text" },
        { palette: ".drag-palette-2", target: ".drag-target-1", typeChip: ".type-chip-1", typeLabel: "Date" },
        { palette: ".drag-palette-3", target: ".drag-target-2", typeChip: ".type-chip-2", typeLabel: "Select" },
        { palette: ".drag-palette-4", target: ".drag-target-3", typeChip: ".type-chip-3", typeLabel: "Yes / No" },
      ];

      cycles.forEach((c, i) => {
        const offset = i * 1.6;
        // 1. Palette item lifts (active state)
        tl.to(
          c.palette,
          {
            scale: 1.06,
            boxShadow: "0 8px 18px -8px rgba(240, 90, 40, 0.55)",
            duration: 0.22,
          },
          offset,
        );
        // 2. Ghost cursor + dragged chip travels to target
        tl.fromTo(
          ".drag-cursor",
          { x: 64, y: 28 + i * 38, opacity: 0 },
          { x: 200, y: 92 + i * 38, opacity: 1, duration: 0.55 },
          offset + 0.05,
        );
        // 3. Target row highlights as drop zone
        tl.to(
          c.target,
          {
            borderColor: "rgba(240, 90, 40, 0.45)",
            backgroundColor: "rgba(240, 90, 40, 0.06)",
            duration: 0.25,
          },
          offset + 0.3,
        );
        // 4. Type chip on the target reveals
        tl.fromTo(
          c.typeChip,
          { opacity: 0, scale: 0.85 },
          { opacity: 1, scale: 1, duration: 0.28 },
          offset + 0.55,
        );
        // 5. Palette item returns to rest
        tl.to(
          c.palette,
          { scale: 1, boxShadow: "0 0 0 0 rgba(0,0,0,0)", duration: 0.22 },
          offset + 0.65,
        );
        // 6. Cursor fades
        tl.to(".drag-cursor", { opacity: 0, duration: 0.2 }, offset + 0.95);
        // 7. Drop-zone subtle settle back
        tl.to(
          c.target,
          {
            borderColor: "rgba(226, 232, 240, 0.7)",
            backgroundColor: "rgba(241, 245, 249, 0.4)",
            duration: 0.35,
          },
          offset + 1.05,
        );
      });
    }, el);

    return () => ctx.revert();
  }, []);

  return (
    <div
      ref={root}
      className="relative overflow-hidden rounded-2xl border border-border/60 bg-surface shadow-[var(--shadow-card)]"
      role="img"
      aria-label="Animated eCRF builder demo: dragging field types from a palette into a form canvas"
    >
      <MockChrome title="eCRF Builder · Adverse Events" />

      <div className="grid grid-cols-[68px_minmax(0,1fr)] gap-1.5 p-2.5 text-[11px] xs:gap-3 xs:p-4 sm:grid-cols-[100px_minmax(0,1fr)]">
        {/* Palette */}
        <div className="rounded-lg border border-border/60 bg-surface-muted/60 p-2">
          <div className="px-1 pb-2 text-[9px] font-semibold uppercase tracking-wider text-muted-foreground">
            Fields
          </div>
          <div className="space-y-1.5">
            {PALETTE.map((p, i) => (
              <div
                key={p.label}
                className={`drag-palette-${i} flex min-w-0 items-center gap-1 rounded-md border border-border/40 bg-surface px-1.5 py-1.5 text-[10px] text-foreground/80 transition-shadow xs:px-2 sm:gap-2 sm:text-[11px]`}
              >
                <p.icon className="h-2.5 w-2.5 shrink-0 text-muted-foreground sm:h-3 sm:w-3" />
                <span className="font-medium">{p.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Form canvas */}
        <div className="rounded-lg border border-border/60 bg-surface">
          <div className="flex items-center justify-between border-b border-border/60 px-3 py-2">
            <div className="flex items-center gap-1.5">
              <span className="font-semibold text-foreground">Adverse Events</span>
              <span className="rounded-full bg-primary/10 px-1.5 py-px text-[9px] font-medium text-primary">
                Visit 2
              </span>
            </div>
            <span className="inline-flex items-center gap-1 text-[9px] font-medium text-success">
              <GitBranch className="h-2.5 w-2.5" />
              4 rules
            </span>
          </div>

          <div className="space-y-2 px-3 py-3">
            {[
              { i: 0, label: "Verbatim term", typeLabel: "Text" },
              { i: 1, label: "Start date", typeLabel: "Date" },
              { i: 2, label: "Severity", typeLabel: "Select" },
              { i: 3, label: "Related to drug", typeLabel: "Yes / No" },
            ].map(({ i, label, typeLabel }) => (
              <div
                key={i}
                className={`drag-target-${i} flex items-center justify-between rounded-md border border-border/50 bg-surface-muted/40 px-2 py-1.5 transition-colors xs:px-2.5`}
              >
                <div className="flex min-w-0 items-center gap-1 xs:gap-1.5">
                  <span className="h-1 w-1 shrink-0 rounded-full bg-primary/60" />
                  <span className="truncate font-medium text-foreground/90">{label}</span>
                  <span className="shrink-0 text-[8px] font-semibold uppercase text-safety">*</span>
                </div>
                <span
                  className={`type-chip-${i} ml-1 shrink-0 rounded-sm border border-border/70 bg-surface px-1 py-px font-mono text-[9px] text-muted-foreground xs:ml-1.5 xs:px-1.5`}
                >
                  {typeLabel}
                </span>
              </div>
            ))}
            <button className="mt-1 flex w-full items-center justify-center gap-1 rounded-md border border-dashed border-border/70 py-1.5 text-[10px] text-muted-foreground transition-colors hover:border-primary/40 hover:text-primary">
              <Plus className="h-3 w-3" />
              Add field
            </button>
          </div>
        </div>

        {/* Floating cursor + ghost-drag chip */}
        <div className="drag-cursor pointer-events-none absolute left-0 top-0 z-10 opacity-0">
          <div className="inline-flex -translate-x-1/2 -translate-y-1/2 items-center gap-1.5 rounded-md border border-primary/40 bg-surface px-2 py-1 text-[10px] font-medium text-primary shadow-[0_8px_18px_-8px_rgba(241,19,1,0.55)]">
            <Plus className="h-3 w-3" />
            Drop
          </div>
        </div>
      </div>
    </div>
  );
}
